import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Flame } from "lucide-react";
import { apiGet } from "@/lib/api";

const FALLBACK = [
  "General $90 — the easiest way to be in the room",
  "Select+ $149 includes the meet & greet + photo op",
  "VIP Experience — first 2 rows + a private after-show group reading",
  "Only 64 VIP Experience seats exist in the whole house",
  "Every candle lit joins the Remembrance moment on January 14",
];

const ActivityToast = () => {
  const [activity, setActivity] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const load = () => apiGet("/api/hype").then((d) => setActivity(d.recent_activity || [])).catch(() => {});
    load();
    const poll = setInterval(load, 45000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (closed) return;
    const cycle = setInterval(() => {
      setVisible(true);
      timer.current = setTimeout(() => setVisible(false), 5000);
    }, 20000);
    const first = setTimeout(() => {
      setVisible(true);
      timer.current = setTimeout(() => setVisible(false), 5000);
    }, 9000);
    return () => {
      clearInterval(cycle);
      clearTimeout(first);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [closed]);

  const items = activity.length
    ? activity.map((a) => ({
        icon: a.is_gift ? "gift" : "ticket",
        text: a.is_gift
          ? `Someone just sent a gift ticket — ${a.count} ${a.zone} seat${a.count > 1 ? "s" : ""}`
          : `Someone just reserved ${a.count} ${a.zone} seat${a.count > 1 ? "s" : ""}`,
        when: a.minutes_ago <= 1 ? "moments ago" : a.minutes_ago < 60 ? `${a.minutes_ago} min ago` : `${Math.floor(a.minutes_ago / 60)}h ago`,
      }))
    : FALLBACK.map((t) => ({ icon: "flame", text: t, when: "live" }));

  const item = items[idx % items.length];

  return (
    <AnimatePresence>
      {visible && !closed && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="activity-toast"
          className="fixed bottom-5 left-5 z-40 max-w-[320px] glass rounded-2xl px-5 py-4 border-[#F3D068]/30 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-start gap-3.5">
            <span className="shrink-0 w-9 h-9 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/40 flex items-center justify-center">
              {item.icon === "gift" ? (
                <span className="text-[#F3D068] text-sm font-bold">G</span>
              ) : item.icon === "flame" ? (
                <Flame size={15} className="text-[#F59E0B] flame-flicker" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-[#34D399] glow-pulse" />
              )}
            </span>
            <div className="flex-1">
              <p className="text-[13px] text-[#FDFBF7] font-light leading-snug">{item.text}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A99B5] mt-1.5">{item.when}</p>
            </div>
            <button
              onClick={() => {
                setVisible(false);
                setClosed(true);
              }}
              aria-label="Dismiss"
              data-testid="activity-toast-dismiss"
              className="text-[#8A99B5] hover:text-[#F3D068] transition-colors p-1"
            >
              <X size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActivityToast;
